import React, {useMemo, useState} from 'react';
import {Checkbox, Menu, MenuItem, Stack} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Icon,
} from '@lightricks/react-design-system';

export interface Option<T = string> {
  id: T;
  label: string;
}

export interface DropdownButtonProps<T extends string> {
  label: string | React.ReactNode;
  options: Option<T>[];
  selectedOptionIds?: string[];
  onChange: (option: Option<T>) => void;
  renderMenuTitle?: () => React.ReactNode;
  onClick?: () => void;
  onClose?: () => void;
  horizontalScroll?: boolean;
}

function DropdownButton<T extends string>(props: DropdownButtonProps<T>) {
  const {
    label,
    options,
    selectedOptionIds = [],
    onChange,
    renderMenuTitle,
    onClick,
    onClose,
    horizontalScroll = false,
  } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    onClick?.();
    setAnchorEl(event.currentTarget);
  };

  const handleOnClose = () => {
    onClose?.();
    setAnchorEl(null);
  };

  const buttonStyle = useMemo(() => {
    let borderColor = designSystemToken('semantic.bg.neutral-inverse');
    if (anchorEl) {
      borderColor = designSystemToken('semantic.fg.primary');
    }

    return {
      width: '256px',
      justifyContent: 'space-between',
      border: `1px solid ${borderColor}`,
      borderRadius: '12px',
    };
  }, [anchorEl]);

  const renderMenu = () => (
    <Menu
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={handleOnClose}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '16px',
          paddingInline: '8px',
          minWidth: '150px',
          width: 'unset',
          maxHeight: '300px',
          maxWidth: '990px',
        },
        '& .MuiMenu-list': {
          display: 'flex',
          flexDirection: 'column',
          gap: '5px',
        },
      }}
    >
      {renderMenuTitle?.()}
      <div
        style={{
          display: 'flex',
          flexDirection: horizontalScroll ? 'row' : 'column',
          gap: '8px',
          ...(horizontalScroll
            ? {
                overflowX: 'auto',
                overflowY: 'hidden',
                maxHeight: '200px',
                maxWidth: '970px',
                alignItems: 'flex-start',
              }
            : {
                overflowX: 'hidden',
                overflowY: 'auto',
                maxHeight: '180px',
              }),
          padding: '8px 0',
        }}
      >
        {options.reduce<React.ReactNode[]>((acc, option, index) => {
          if (index % 4 === 0) {
            acc.push(
              <Stack
                key={option.id}
                direction={horizontalScroll ? 'column' : 'row'}
                spacing={horizontalScroll ? 1 : 2}
              >
                {options.slice(index, index + 4).map((selectedOption) => (
                  <MenuItem
                    key={selectedOption.id}
                    sx={{
                      width: '228px',
                      maxHeight: '40px',
                      minHeight: '40px',
                    }}
                    onClick={() => onChange(selectedOption)}
                  >
                    <Checkbox
                      checked={selectedOptionIds.includes(selectedOption.id)}
                    />
                    <Body size="lg" textOverflow="ellipsis" overflow="hidden">
                      {selectedOption.label}
                    </Body>
                  </MenuItem>
                ))}
              </Stack>
            );
          }
          return acc;
        }, [])}
      </div>
    </Menu>
  );

  return (
    <>
      <Button
        onClick={handleOnClick}
        sx={buttonStyle}
        appearance="neutral"
        mode="filled"
        size="medium"
        endIcon={
          <Icon
            className={anchorEl ? 'iconOpen' : ''}
            size="small"
            appearance="neutral"
            name="Actions-Arrow-Down-Large"
            color={designSystemToken('semantic.fg.inverse')}
          />
        }
      >
        {label}
      </Button>
      {renderMenu()}
    </>
  );
}

export default DropdownButton;
